import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", message: "Confirming your payment..." });
  const [attempt, setAttempt] = useState(0);
  const { refreshMe } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session_id." });
      return;
    }
    let cancelled = false;
    const maxAttempts = 10;
    const poll = async (a = 0) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setAttempt(a + 1);
        if (data.payment_status === "paid") {
          await refreshMe();
          setState({ status: "success", message: "Payment confirmed! Course unlocked.", courseId: data.course_id });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "error", message: "Session expired. Please try again." });
          return;
        }
        if (a + 1 >= maxAttempts) {
          setState({ status: "pending", message: "Still processing. Check your dashboard in a moment." });
          return;
        }
        setTimeout(() => poll(a + 1), 2000);
      } catch (e) {
        if (!cancelled) setState({ status: "error", message: formatApiError(e) });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  useEffect(() => {
    if (state.status !== "success" || !state.courseId) return;
    const t = setTimeout(() => nav(`/course/${state.courseId}`, { state: { justPurchased: true } }), 3500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [state.status]);

  return (
    <div className="bg-grain min-h-screen grid place-items-center p-6">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 22 }}
        className="card max-w-md w-full text-center" data-testid="payment-success-card"
      >
        {state.status === "polling" && (
          <>
            <div className="grid place-items-center my-3">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}>
                <Spinner size={56} weight="duotone" color="#00E5FF" />
              </motion.div>
            </div>
            <h2 className="text-2xl font-bold font-display mb-1">{state.message}</h2>
            <p className="text-[#000000] text-sm">Attempt {attempt}/10</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle size={72} weight="fill" color="#CCFF00" className="mx-auto" />
            <h2 className="text-3xl font-extrabold font-display mt-3 mb-1">Welcome aboard!</h2>
            <p className="text-[#000000] mb-1">{state.message}</p>
            <p className="text-xs text-[#94979C] mb-2" data-testid="receipt-note">A receipt is on its way to your inbox.</p>
            <p className="text-sm font-semibold text-[#000000] mb-4" data-testid="auto-redirect-note">Taking you straight into your first lesson…</p>
            <button data-testid="open-course-button" className="btn-primary w-full" onClick={() => nav(`/course/${state.courseId}`, { state: { justPurchased: true } })}>Start learning now →</button>
            <button className="btn-ghost mt-2 w-full" onClick={() => nav("/dashboard")}>Back to dashboard</button>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Spinner size={56} weight="duotone" color="#FFD600" className="mx-auto" />
            <h2 className="text-2xl font-bold font-display mt-3 mb-1">Still processing</h2>
            <p className="text-[#000000] mb-4">{state.message}</p>
            <button className="btn-primary w-full" onClick={() => nav("/dashboard")}>Back to dashboard</button>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle size={56} weight="fill" color="#FF5100" className="mx-auto" />
            <h2 className="text-2xl font-bold font-display mt-3 mb-1">Payment issue</h2>
            <p className="text-[#FF5100] mb-4">{state.message}</p>
            <button className="btn-primary w-full" onClick={() => nav("/catalog")}>Back to catalog</button>
          </>
        )}
      </motion.div>
    </div>
  );
}
